import { useState } from 'react'
import { type FieldValues, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'

import { useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { ReactComponent as RightArrow } from '../../components/assets/icons/rightArrow.svg'
import MainButton from '../../components/Button/MainButton'
import Header from '../../components/header/Header'
import MainText from '../../components/header/MainText'
import { API_URL } from '../../components/helpers/Helpers'
import SimpleInput from '../../components/inputs/SimpleInput'
import Wrapper from '../../components/wrapper/Wrapper'

type Debt = {
  id: string
  plate: string
  operation_id: string
  operation: string
  notified_at: string
  price?: number
}

function RegularizarToken() {
  const toast = useToast()
  const { tokenId } = useParams<{ tokenId: string }>()
  const [debts, setDebts] = useState<Debt[]>([])
  const [selectedDebts, setSelectedDebts] = useState<Set<string>>(new Set())
  const [loadingDebts, setLoadingDebts] = useState<Set<string>>(new Set())
  const [totalPrice, setTotalPrice] = useState(0)
  const [showDebts, setShowDebts] = useState(false)
  const [isPaying, setIsPaying] = useState(false)

  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        code: yup
          .string()
          .matches(/^\d{5}$/, 'O código deve ter exatamente 5 dígitos')
          .required('Código é obrigatório'),
      }),
    ),
  })

  async function submitCode(payload: FieldValues) {
    try {
      const response = await fetch(`${API_URL}/api/regularization-pix-verify`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: tokenId, code: payload.code }),
      })

      const data = await response.json()

      if (data.ok) {
        setDebts(data.data || [])
        setShowDebts(true)
      } else {
        console.error(data)
        toast({ title: 'Código inválido ou expirado', status: 'error', duration: 3000, isClosable: true })
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error)
      toast({ title: 'Erro ao verificar código. Tente novamente.', status: 'error', duration: 3000, isClosable: true })
    }
  }

  async function handlePixPayment() {
    setIsPaying(true)

    try {
      const response = await fetch(`${API_URL}/api/acts/payments/pix`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ total_price: totalPrice, act_ids: Array.from(selectedDebts) }),
      })

      const data = await response.json()

      if (response.ok) {
        navigate(`/regularizar/pix/${data.data.id}`)
      } else {
        toast({ title: 'Erro ao realizar pagamento', status: 'error', duration: 3000, isClosable: true })
        console.error(data)
      }
    } catch (error) {
      console.error('Erro ao realizar pagamento:', error)
      toast({
        title: 'Erro ao realizar pagamento. Tente novamente.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsPaying(false)
    }
  }

  async function selectDebt(debt: Debt) {
    if (loadingDebts.has(debt.id)) {
      return
    }

    if (selectedDebts.has(debt.id)) {
      setSelectedDebts((prev) => {
        const newSet = new Set(prev)
        newSet.delete(debt.id)
        return newSet
      })

      setTotalPrice((prev) => prev - (debt.price || 0))
    } else {
      setLoadingDebts((prev) => new Set([...prev, debt.id]))

      try {
        const response = await fetch(`${API_URL}/api/regularization-pix-price`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ plate: debt.plate, id: debt.id, operation_id: debt.operation_id }),
        })

        const data = await response.json()

        if (response.ok) {
          const price = data.data.price

          setDebts((prev) => prev.map((d) => (d.id === debt.id ? { ...d, price } : d)))
          setSelectedDebts((prev) => new Set([...prev, debt.id]))
          setTotalPrice((prev) => prev + price)
        } else {
          console.error(data)
          toast({ title: data?.error?.message || 'Erro ao obter preço do ACT', status: 'error', duration: 3000, isClosable: true })
        }
      } catch (error) {
        console.error('Erro ao consultar preço:', error)
        toast({ title: 'Erro ao consultar preço. Tente novamente.', status: 'error', duration: 3000, isClosable: true })
      } finally {
        setLoadingDebts((prev) => {
          const newSet = new Set(prev)
          newSet.delete(debt.id)
          return newSet
        })
      }
    }
  }

  function formatCurrency(value: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100)
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  return (
    <Wrapper>
      <Header />
      {!showDebts ? (
        <>
          <MainText text="Digite o código de 5 dígitos recebido no seu telefone." />
          <form
            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '30px' }}
            onSubmit={handleSubmit(submitCode)}
          >
            <SimpleInput
              isVehicle
              title="CÓDIGO DE VERIFICAÇÃO"
              register={register}
              value={watch('code')}
              name="code"
              error={errors.code}
              placeholder="Digite o código de 5 dígitos"
            />

            <MainButton type="submit" text="Verificar código" icon={RightArrow} isLoading={isSubmitting} />
          </form>
        </>
      ) : (
        <>
          <MainText text="Selecione os ACTs que deseja regularizar." />

          {debts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
              <p>Nenhum ACT encontrado para este veículo.</p>
            </div>
          ) : (
            <>
              <div
                style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '20px' }}
              >
                {debts.map((debt) => (
                  <button
                    key={debt.id}
                    type="button"
                    onClick={() => selectDebt(debt)}
                    style={{
                      padding: '15px',
                      border: selectedDebts.has(debt.id) ? '2px solid #007bff' : '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedDebts.has(debt.id) ? '#f0f8ff' : '#fff',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      textAlign: 'left',
                      outline: 'none',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Placa: {debt.plate}</div>
                        <div style={{ color: '#666', fontSize: '14px' }}>Cidade: {debt.operation}</div>
                        <div style={{ color: '#666', fontSize: '14px' }}>
                          Notificado em: {formatDate(debt.notified_at)}
                        </div>
                      </div>

                      <div style={{ textAlign: 'right' }}>
                        {selectedDebts.has(debt.id) && debt.price && (
                          <div style={{ fontWeight: 'bold', color: '#007bff', fontSize: '16px' }}>
                            {formatCurrency(debt.price)}
                          </div>
                        )}

                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            border: '2px solid #007bff',
                            borderRadius: '50%',
                            marginTop: '5px',
                            marginLeft: 'auto',
                            backgroundColor: selectedDebts.has(debt.id) ? '#007bff' : 'transparent',
                            position: 'relative',
                          }}
                        >
                          {loadingDebts.has(debt.id) && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '12px',
                                height: '12px',
                                border: '2px solid transparent',
                                borderTop: '2px solid #007bff',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {selectedDebts.size > 0 && (
                <div
                  style={{
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                    Total a pagar: {formatCurrency(totalPrice)}
                  </div>
                  <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                    {selectedDebts.size} ACT{selectedDebts.size > 1 ? 's' : ''} selecionado
                    {selectedDebts.size > 1 ? 's' : ''}
                  </div>
                </div>
              )}

              {selectedDebts.size > 0 && (
                <MainButton
                  text={`Pagar ${formatCurrency(totalPrice)}`}
                  icon={RightArrow}
                  onClick={handlePixPayment}
                  isLoading={isPaying}
                />
              )}
            </>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default RegularizarToken
